import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './PromoCodeList.css';

interface PromoCode {
  id: string;
  code: string;
  couponTemplate: string;
  campaign: string;
  channel: string;
  status: string;
  redemptions: number;
  maxRedemptions: number | null;
  createdAt: string;
}

interface Filters {
  campaign: string;
  channel: string;
  status: string;
}

const CHANNEL_OPTIONS = [
  { value: '', label: 'All Channels' },
  { value: 'email', label: 'Email' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'partner', label: 'Partner' },
  { value: 'event', label: 'Event' },
  { value: 'direct_mail', label: 'Direct Mail' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' },
];

const PromoCodeList: React.FC = () => {
  const { getAccessToken } = useAuth();

  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({ campaign: '', channel: '', status: '' });
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const buildQueryParams = useCallback(
    (paginate: boolean) => {
      const params = new URLSearchParams();
      if (filters.campaign.trim()) params.set('campaign', filters.campaign.trim());
      if (filters.channel) params.set('channel', filters.channel);
      if (filters.status) params.set('status', filters.status);
      if (paginate && lastKey) params.set('lastKey', lastKey);
      return params.toString();
    },
    [filters, lastKey]
  );

  const loadCodes = useCallback(
    async (paginate = false) => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessToken();
        if (!token) {
          setError('Authentication expired. Please log in again.');
          return;
        }

        const qs = buildQueryParams(paginate);
        const url = API_ENDPOINTS.promoCodes() + (qs ? `?${qs}` : '');

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.message || `Failed to load promo codes (${response.status})`);
        }

        const data = await response.json();
        const incoming = data.promoCodes || data.items || [];

        if (paginate) {
          setCodes((prev) => [...prev, ...incoming]);
        } else {
          setCodes(incoming);
        }

        setLastKey(data.lastKey || null);
      } catch (err: any) {
        setError(err.message || 'Failed to load promo codes.');
      } finally {
        setLoading(false);
      }
    },
    [getAccessToken, buildQueryParams]
  );

  useEffect(() => {
    loadCodes(false);
  }, []);

  const handleFilterApply = () => {
    setSelectedIds(new Set());
    setLastKey(null);
    loadCodes(false);
  };

  const handleLoadMore = () => {
    loadCodes(true);
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === codes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(codes.map((c) => c.id)));
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedIds.size === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to deactivate ${selectedIds.size} promo code(s)? This action cannot be undone.`
    );
    if (!confirmed) return;

    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Authentication expired. Please log in again.');
        return;
      }

      const deactivatePromises = Array.from(selectedIds).map((id) =>
        fetch(API_ENDPOINTS.promoCodeDeactivate(id), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })
      );

      const results = await Promise.allSettled(deactivatePromises);
      const failCount = results.filter((r) => r.status === 'rejected').length;

      if (failCount > 0) {
        setError(`${failCount} code(s) failed to deactivate. Please retry.`);
      }

      setSelectedIds(new Set());
      loadCodes(false);
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate selected codes.');
    }
  };

  const handleExportCsv = async () => {
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Authentication expired. Please log in again.');
        return;
      }

      const response = await fetch(API_ENDPOINTS.promoExport(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign: filters.campaign.trim() || undefined,
          channel: filters.channel || undefined,
          status: filters.status || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `Export failed (${response.status})`);
      }

      const data = await response.json();
      const rows = data.promoCodes || data.items || codes;

      // Build CSV in memory
      const headers = ['Code', 'Template', 'Campaign', 'Channel', 'Status', 'Redemptions', 'Max Redemptions', 'Created'];
      const csvLines = [headers.join(',')];

      rows.forEach((row: any) => {
        csvLines.push(
          [
            `"${row.code || ''}"`,
            `"${row.couponTemplate || ''}"`,
            `"${row.campaign || ''}"`,
            `"${row.channel || ''}"`,
            `"${row.status || ''}"`,
            row.redemptions ?? 0,
            row.maxRedemptions ?? 'Unlimited',
            `"${row.createdAt || ''}"`,
          ].join(',')
        );
      });

      const csvString = csvLines.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `promo-codes-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to export promo codes.');
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'active':
        return 'status-badge active';
      case 'inactive':
        return 'status-badge inactive';
      case 'expired':
        return 'status-badge expired';
      default:
        return 'status-badge';
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="promo-list">
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>x</button>
        </div>
      )}

      <div className="list-filters">
        <input
          type="text"
          placeholder="Filter by campaign..."
          value={filters.campaign}
          onChange={(e) => setFilters({ ...filters, campaign: e.target.value })}
        />
        <select
          value={filters.channel}
          onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
        >
          {CHANNEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button className="filter-apply-btn" onClick={handleFilterApply}>
          Apply
        </button>
      </div>

      <div className="bulk-actions">
        <button
          className="bulk-deactivate-btn"
          onClick={handleBulkDeactivate}
          disabled={selectedIds.size === 0}
        >
          Deactivate Selected ({selectedIds.size})
        </button>
        <button className="export-btn" onClick={handleExportCsv}>
          Export CSV
        </button>
      </div>

      {loading && codes.length === 0 && <p className="loading-text">Loading promo codes...</p>}

      {!loading && codes.length === 0 && !error && (
        <p className="empty-text">No promo codes found. Use the Generator tab to create codes.</p>
      )}

      {codes.length > 0 && (
        <table className="promo-table">
          <thead>
            <tr>
              <th className="checkbox-col">
                <input
                  type="checkbox"
                  checked={selectedIds.size === codes.length && codes.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Code</th>
              <th>Type</th>
              <th>Campaign</th>
              <th>Channel</th>
              <th>Status</th>
              <th>Redemptions</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((item) => (
              <tr key={item.id}>
                <td className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleSelect(item.id)}
                  />
                </td>
                <td>
                  <span
                    className="code-cell"
                    onClick={() => handleCopyCode(item.code)}
                    title="Click to copy"
                  >
                    {item.code}
                    {copiedCode === item.code && <span className="copied-indicator">Copied</span>}
                  </span>
                </td>
                <td>{item.couponTemplate || '-'}</td>
                <td>{item.campaign || '-'}</td>
                <td>{item.channel || '-'}</td>
                <td>
                  <span className={getStatusBadgeClass(item.status)}>
                    {item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : '-'}
                  </span>
                </td>
                <td>
                  {item.redemptions ?? 0} / {item.maxRedemptions ?? 'Unlimited'}
                </td>
                <td>{formatDate(item.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {lastKey && (
        <div className="load-more-container">
          <button className="load-more-btn" onClick={handleLoadMore} disabled={loading}>
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PromoCodeList;
